"use client"
import { useState } from "react"
import { Content, isFilled, asLink } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import Grid from "@aura-design/system/grid"
import Modal from "@aura-design/system/modal"
import { PrismicRichText } from "@airplan/ui/prismic-richtext"
import { useRouter } from 'next/navigation'

/**
 * Props for `CardSwitchButton`.
 */
export type CardSwitchButtonProps =
  SliceComponentProps<Content.CardSwitchButtonSlice>

/**
 * Component for "CardSwitchButton" Slices.
 */
const CardSwitchButton = ({ slice }: CardSwitchButtonProps): JSX.Element => {
  const [select, setSelect] = useState(null)
  const router = useRouter()

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pb-4"
    >
      <Modal isVisible={Boolean(select)} onClose={() => setSelect(null)}>
        <Grid col="four">
          <div className="span-1">
            {isFilled.image(select?.image) && (
              <PrismicNextImage
                field={select.image}
                fallbackAlt=""
                imgixParams={{
                  txt: select?.copyright,
                  txtAlign: ["bottom", "center"],
                  txtSize: 10,
                }}
              />
            )}
          </div>
          <div className="span-3 content">
            {isFilled.richText(select?.content) && (
              <PrismicRichText field={select?.content} />
            )}
          </div>
        </Grid>
      </Modal>
      {slice.items.length > 0 ? (
        <Grid col={slice.primary.column} className="g0">
          {slice.items.map((item, index) => {
            const isEven = slice.primary.invert
              ? index % 2 === 0
              : index % 2 !== 0
            const hasLink = isFilled.link(item.button_link)
            const hasContent = isFilled.richText(item.content)

            const handleOnClick = (item) => {
              if (hasLink) {
                router.push(asLink(item.button_link))
                return
              }
              if (hasContent) {
                setSelect(item)
                return
              }
            }

            const containerClassConnect: string[] = [
              "wall-pad",
              "flex",
              "flex-col",
              "justify-between",
              "border-0",
              "cursor-pointer",
              "hover:opacity-90",
            ]

            const imageClassConnect: string[] = ["object-cover", "halo"]

            if (isEven) {
              containerClassConnect.push("bg-dark-blue text-white")
              imageClassConnect.push("rounded-t-2xl")
            } else {
              containerClassConnect.push("bg-light-gray md:flex-col-reverse")
              imageClassConnect.push("rounded-b-2xl")
            }

            return (
              <button
                className={containerClassConnect.join(" ").trim()}
                onClick={() => handleOnClick(item)}
                key={index}
              >
                {isFilled.richText(item.title) && (
                  <div className="fluid py-2">
                    <h3 className="h5">
                      {item.title.map(
                        (richText: Record<string, any>, index: number) => {
                          const isFirst = index === 0
                          const classNameContainer: string[] = []

                          if (!isFirst) {
                            classNameContainer.push("wall-pad rounded-md")
                            if (isFilled.select(item.color)) {
                              classNameContainer.push(item.color)
                              classNameContainer.push("text-white")
                            } else {
                              classNameContainer.push("bg-primary")
                              classNameContainer.push("text-white")
                            }
                          }

                          return (
                            <span
                              key={index}
                              className={classNameContainer.join(" ").trim()}
                            >
                              {richText.text}
                              <br />
                            </span>
                          )
                        },
                      )}
                    </h3>
                  </div>
                )}
                {isFilled.image(item.image) && (
                  <PrismicNextImage
                    className={imageClassConnect.join(" ").trim()}
                    field={item.image}
                    fallbackAlt=""
                    imgixParams={{
                      txt: item.image?.copyright,
                      txtAlign: ["bottom", "center"],
                      txtSize: 10,
                    }}
                  />
                )}
              </button>
            )
          })}
        </Grid>
      ) : null}
    </section>
  )
}

export default CardSwitchButton
